// src/app/router.jsx
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import AppShell from '@app/AppShell';
import { NotificationProvider } from './notifications/NotificationProvider';
import { LoadingProvider } from './loading/LoadingProvider';

// Lazy-loaded pages
const SignInPage = lazy(() => import('@pages/auth/Login'));
const ActivatePage = lazy(() => import('@pages/auth/Activate'));
const ResetPasswordPage = lazy(() => import('@pages/auth/ResetPasswordPage'));
const HomePage = lazy(() => import('@pages/HomePage'));
const VendorsListPage = lazy(() => import('@pages/vendors/VendorsListPage'));
const CreateVendorPage = lazy(() => import('@pages/vendors/CreateVendorPage'));
const DerbyStatusManagementPage = lazy(() => import('@pages/vendors/DerbyStatusManagementPage'));
const SavedPage = lazy(() => import('@pages/vendors/SavedCheckInTransactionsPage'));
const FoliosPage = lazy(() => import('@pages/vendors/CheckInTransactionsPage'));
const MsiTransactionLogPage = lazy(() => import('@pages/msi/MsiTransactionLogPage'));
const MsiTransactionPage = lazy(() => import('@pages/msi/MsiTransactionPage'));
const EmployeesListPage = lazy(() => import('@pages/employees/EmployeeListPage'));
const EmployeeImportPage = lazy(() => import('@pages/employees/EmployeeImportPage'));
const HotelSearchPage = lazy(() => import('@pages/bookings/HotelSearchPage'));
const HotelDetailPage = lazy(() => import('@pages/bookings/HotelDetailPage'));
const BookingGuestDetailsPage = lazy(() => import('@pages/bookings/BookingGuestDetailsPage'));
const BookingConfirmationPage = lazy(() => import('@pages/bookings/BookingConfirmationPage'));
const BookingFinalConfirmationPage = lazy(() => import('@pages/bookings/BookingFinalConfirmationPage'));
const BookingPaymentPage = lazy(() => import('@pages/bookings/BookingPaymentPage'));
const MyReservationsPage = lazy(() => import('@pages/bookings/MyReservationsPage'));
const CustomerFeesPage = lazy(() => import('@pages/fees/CustomerFeesPage'));
const ChainsPage = lazy(() => import('@pages/chains/ChainsPage'));
const ProfilePage = lazy(() => import('@pages/users/ProfilePage'));
const CheckInPage = lazy(() => import('@pages/vendors/CheckIn'));
const UserManagement = lazy(() => import('../features/users/components/UserManagement'));
const BillingPage = lazy(() => import('@pages/billing/BillingPage'));
const BillingBatchDetailPage = lazy(() => import('@pages/billing/BillingBatchDetailPage'));
const BillingBatchesPage = lazy(() => import('@pages/billing/BillingBatchesPage'));
const RecordPaymentPage = lazy(() => import('@pages/billing/RecordPaymentPage'));
const RecordMultipleBatchPaymentPage = lazy(() => import('@pages/billing/RecordMultipleBatchPaymentPage'));
const PaymentExceptionsPage = lazy(() => import('@pages/billing/PaymentExceptionsPage'));
const StaffReservationsPage = lazy(() => import('@pages/staff-reservations/StaffReservationsPage'));
const CreateManualReservationPage = lazy(() => import('@pages/staff-reservations/CreateManualReservationPage'));
const IntegrationsPage = lazy(() => import('@pages/integrations/IntegrationsPage'));
import AuthProvider, { useAuth } from '@app/providers/AuthProvider';
import { BookingFlowProvider } from '@features/bookings/context/BookingFlowContext';

const PageLoader = () => <div style={{ padding: '2rem', textAlign: 'center' }}>Loading…</div>;
const withSuspense = (Component) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
);

// --- wrappers ---
function WithAuthProvider() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <LoadingProvider>
          <Outlet />
        </LoadingProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

function RequirePermission({ ability }) {
  const { user } = useAuth();
  const has = !!user?.roles?.some((r) =>
    r?.permissions?.some((p) => p?.name === ability)
  );
  return has ? <Outlet /> : <Navigate to="/user-home" replace />;
}

function Protected() {
  const { user, checking } = useAuth();
  if (checking) return null;
  return user ? <Outlet /> : <Navigate to="/login" replace />;
}

function BookingFlowProviderOutlet() {
  return (
    <BookingFlowProvider>
      <Outlet />
    </BookingFlowProvider>
  );
}

// --- router ---
const routes = [
  {
    element: <WithAuthProvider />,
    children: [
      { path: '/login', element: withSuspense(SignInPage) },
      { path: '/activate', element: withSuspense(ActivatePage) },
      { path: '/reset-password', element: withSuspense(ResetPasswordPage) },

      {
        element: <Protected />,
        children: [
          {
            path: '/',
            element: <AppShell />,
            children: [
              {
                path: 'user-home',
                element: withSuspense(HomePage),
                handle: { crumb: 'Home', title: 'Home' },
              },
              {
                path: 'profile',
                element: withSuspense(ProfilePage),
                handle: { crumb: 'Profile', title: 'Profile Overview' },
              },
              {
                element: <RequirePermission ability="book reservations" />,
                children: [
                  {
                    path: 'reservations',
                    element: withSuspense(MyReservationsPage),
                    handle: { crumb: 'Reservations', title: 'My Reservations' },
                  },
                ],
              },
       
              {
                element: <RequirePermission ability="view users" />,
                children: [
                  {
                    path: 'users',
                    element: withSuspense(UserManagement),
                    handle: { crumb: 'User Management', title: 'User Management' },
                  },
                ],
              },
              {
                element: <RequirePermission ability="manage hotel chains" />,
                children: [
                  {
                    path: 'chains',
                    element: withSuspense(ChainsPage),
                    handle: { crumb: 'Hotel Chains', title: 'Hotel Chains' },
                  },
                ],
              },
              {
                element: <RequirePermission ability="view vendors" />,
                children: [
                  {
                    path: 'vendors',
                    element: withSuspense(VendorsListPage),
                    handle: { crumb: 'Vendors', title: 'Vendors' },
                  },
                ],
              },
              {
                element: <RequirePermission ability="create vendors" />,
                children: [
                  {
                    path: 'vendors/create',
                    element: withSuspense(CreateVendorPage),
                    handle: {
                      crumbs: [
                        { label: 'Vendors', to: '/vendors' },
                        { label: 'Create Vendor' },
                      ],
                      title: 'Create Vendor',
                    },
                  },
                ],
              },
              {
                element: <RequirePermission ability="edit vendors" />,
                children: [
                  {
                    path: 'vendors/derby-status',
                    element: withSuspense(DerbyStatusManagementPage),
                    handle: {
                      crumbs: [
                        { label: 'Vendors', to: '/vendors' },
                        { label: 'Derby Status' },
                      ],
                      title: 'Derby Status',
                    },
                  },
                  {
                    path: 'vendors/:id/edit',
                    element: withSuspense(CreateVendorPage),
                    handle: {
                      crumbs: [
                        { label: 'Vendors', to: '/vendors' },
                        { label: 'Edit Vendor' },
                      ],
                      title: 'Edit Vendor',
                    },
                  },
                ],
              },
              {
                element: <RequirePermission ability="view check-ins" />,
                children: [
                  {
                    path: 'saved',
                    element: withSuspense(SavedPage),
                    handle: { crumb: 'Transaction Drafts', title: 'Transaction Drafts' },
                  },
                  {
                    path: 'folios',
                    element: withSuspense(FoliosPage),
                    handle: { crumb: 'Transaction Status', title: 'Transaction Status' },
                  },
                ],
              },
              {
                element: <RequirePermission ability="create check-ins" />,
                children: [
                  {
                    path: 'check-in',
                    element: withSuspense(CheckInPage),
                    handle: { crumb: 'Employee Check-In', title: 'Employee Check-In' },
                  },
                  {
                    path: 'check-in/:id',
                    element: withSuspense(CheckInPage),
                    handle: {
                      crumbs: [
                        { label: 'Employee Check-In', to: '/check-in' },
                        { label: 'Check-In Details' },
                      ],
                      title: 'Check-In Details',
                    },
                  },
                ],
              },
              {
                element: <RequirePermission ability="view transactions" />,
                children: [
                  {
                    path: 'transactions/log',
                    element: withSuspense(MsiTransactionLogPage),
                    handle: { crumb: 'Transaction Log', title: 'Transaction Log' },
                  },
                  {
                    path: 'billing',
                    element: withSuspense(BillingPage),
                    handle: { crumb: 'Billing', title: 'Billing Batches' },
                  },
                  {
                    path: 'billing/batches',
                    element: withSuspense(BillingBatchesPage),
                    handle: { crumb: 'Batches', title: 'All Batches' },
                  },
                  {
                    path: 'billing/record-payment',
                    element: withSuspense(RecordPaymentPage),
                    handle: { crumb: 'Record Payment', title: 'Record Payment' },
                  },
                  {
                    path: 'billing/record-payment/process',
                    element: withSuspense(RecordMultipleBatchPaymentPage),
                    handle: { crumb: 'Process Payment', title: 'Process Payment' },
                  },
                  {
                    path: 'billing/:id',
                    element: withSuspense(BillingBatchDetailPage),
                    handle: {
                      crumbs: [
                        { label: 'Billing', to: '/billing' },
                        { label: 'Batch Detail' },
                      ],
                      title: 'Billing Batch',
                    },
                  },
                ],
              },
              {
                element: <RequirePermission ability="create transactions" />,
                children: [
                  {
                    path: 'msi-transactions',
                    element: withSuspense(MsiTransactionPage),
                    handle: { crumb: 'Transactions', title: 'Transactions' },
                  },
                ],
              },
              {
                element: <RequirePermission ability="view employees" />,
                children: [
                  {
                    path: 'employees',
                    element: withSuspense(EmployeesListPage),
                    handle: { crumb: 'Employees', title: 'Employees' },
                  },
                  {
                    path: 'staff-reservations',
                    element: withSuspense(StaffReservationsPage),
                    handle: { crumb: 'Staff Reservations', title: 'Staff Reservations' },
                  },
                  {
                    path: 'staff-reservations/create',
                    element: withSuspense(CreateManualReservationPage),
                    handle: { crumb: 'Create Reservation', title: 'Create Manual Reservation' },
                  },
                  {
                    path: 'staff-reservations/:id/edit',
                    element: withSuspense(CreateManualReservationPage),
                    handle: { crumb: 'Edit Reservation', title: 'Edit Manual Reservation' },
                  },
                ],
              },
              {
                element: <RequirePermission ability="create employees" />,
                children: [
                  // /employees/create and /employees/:id/edit removed —
                  // both flows now happen in a modal on /employees.
                  {
                    path: 'employees/imports',
                    element: withSuspense(EmployeeImportPage),
                    handle: {
                      crumbs: [
                        { label: 'Employees', to: '/employees' },
                        { label: 'Import Employees' },
                      ],
                      title: 'Import Employees',
                    },
                  },
                ],
              },
              {
                element: <RequirePermission ability="manage customer fees" />,
                children: [
                  {
                    path: 'customer-fees',
                    element: withSuspense(CustomerFeesPage),
                    handle: { crumb: 'Customer Fees', title: 'Customer Fees' },
                  },
                ],
              },
              {
                element: <RequirePermission ability="view transactions" />,
                children: [
                  {
                    path: 'integrations',
                    element: withSuspense(IntegrationsPage),
                    handle: { crumb: 'Integrations', title: 'Integrations' },
                  },
                  {
                    path: 'payment-exceptions',
                    element: withSuspense(PaymentExceptionsPage),
                    handle: { crumb: 'Payment Exceptions', title: 'Payment Exceptions' },
                  },
                ],
              },
              { index: true, element: <Navigate to="user-home" replace /> },
              {
                element: <BookingFlowProviderOutlet />,
                children: [
                  {
                    path: 'booking',
                    element: withSuspense(HotelSearchPage),
                    handle: { crumb: 'Reservation', title: 'Reservation Search' },
                  },
                  {
                    path: 'booking/:hotelId',
                    element: withSuspense(HotelDetailPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Hotel Details' },
                      ],
                      title: 'Hotel Details',
                    },
                  },
                  {
                    path: 'booking/:hotelId/guests',
                    element: withSuspense(BookingGuestDetailsPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Guest Details' },
                      ],
                      title: 'Guest Details',
                    },
                  },
                  {
                    path: 'booking/confirmation/:confirmationId',
                    element: withSuspense(BookingFinalConfirmationPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Confirmation' },
                      ],
                      title: 'Confirmation',
                    },
                  },
                  {
                    path: 'booking/:hotelId/confirmation',
                    element: withSuspense(BookingConfirmationPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Confirm Reservation' },
                      ],
                      title: 'Confirm Reservation',
                    },
                  },
                  {
                    path: 'booking/:hotelId/payment',
                    element: withSuspense(BookingPaymentPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Payment' },
                      ],
                      title: 'Payment',
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

const router = createBrowserRouter(routes);

export default router;
export { routes };
